import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { bankAccountsApi, banksApi } from '../../services/adminApi';

const BankAccountForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    bank_id: '',
    iban: '',
    currency: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [banks, setBanks] = useState([]);

  useEffect(() => {
    fetchBanks();
    if (isEdit) {
      fetchAccount();
    }
  }, [id]);

  const fetchBanks = async () => {
    try {
      const response = await banksApi.getAll();
      if (response.data.success) {
        setBanks(response.data.data || []);
      }
    } catch (err) {
      console.error('Banks fetch error:', err);
    }
  };

  const fetchAccount = async () => {
    try {
      setLoading(true);
      const response = await bankAccountsApi.getById(id);
      if (response.data.success) {
        const account = response.data.data;
        setFormData({
          bank_id: account.bank_id || '',
          iban: account.iban || '',
          currency: account.currency || '',
          is_active: account.is_active !== undefined ? account.is_active : true
        });
      } else {
        setError('Banka hesabı bulunamadı');
      }
    } catch (err) {
      console.error('Account fetch error:', err);
      setError('Banka hesabı yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.bank_id) {
      setError('Banka seçimi gereklidir');
      return;
    }
    if (!formData.iban.trim()) {
      setError('IBAN gereklidir');
      return;
    }
    if (!formData.currency.trim()) {
      setError('Para birimi gereklidir');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let response;
      if (isEdit) {
        response = await bankAccountsApi.update(id, formData);
      } else {
        response = await bankAccountsApi.create(formData);
      }

      if (response.data.success) {
        alert(isEdit ? 'Banka hesabı başarıyla güncellendi!' : 'Banka hesabı başarıyla eklendi!');
        navigate('/admin/bank-accounts');
      } else {
        setError(response.data.message || 'Bir hata oluştu');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = {
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
    minHeight: '100vh'
  };

  const formContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    padding: '2rem',
    maxWidth: '48rem',
    margin: '0 auto'
  };

  const titleStyle = {
    fontSize: '1.875rem',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '2rem',
    textAlign: 'center'
  };

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    lineHeight: '1.5',
    color: '#111827',
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    boxSizing: 'border-box'
  };

  const ibanInputStyle = {
    ...inputStyle,
    fontFamily: 'monospace',
    letterSpacing: '0.05em'
  };

  const checkboxContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '2rem'
  };

  const checkboxStyle = {
    marginRight: '0.75rem',
    width: '1.25rem',
    height: '1.25rem',
    cursor: 'pointer'
  };

  const checkboxLabelStyle = {
    ...labelStyle,
    marginBottom: 0,
    cursor: 'pointer'
  };

  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    marginTop: '2rem'
  };

  const buttonStyle = {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    transition: 'all 0.15s ease-in-out',
    border: 'none',
    minWidth: '7rem'
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db'
  };

  const submitButtonStyle = {
    ...buttonStyle,
    backgroundColor: loading ? '#9ca3af' : '#3b82f6',
    color: 'white',
    border: 'none'
  };

  const errorStyle = {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '0.75rem',
    borderRadius: '0.375rem',
    marginBottom: '1.5rem'
  };

  const loadingStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '16rem'
  };

  const spinnerStyle = {
    width: '2rem',
    height: '2rem',
    border: '2px solid #e5e7eb',
    borderTop: '2px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  if (loading && !isEdit) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>
          <div style={spinnerStyle}></div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEdit ? 'Banka Hesabı Düzenle' : 'Yeni Banka Hesabı Ekle'} - Admin Panel</title>
      </Helmet>
      <div style={containerStyle}>
        <div style={formContainerStyle}>
          <h1 style={titleStyle}>
            {isEdit ? 'Banka Hesabı Düzenle' : 'Yeni Banka Hesabı Ekle'}
          </h1>

          {error && (
            <div style={errorStyle}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={formGroupStyle}>
              <label htmlFor="bank_id" style={labelStyle}>
                Banka Seçimi *
              </label>
              <select
                id="bank_id"
                name="bank_id"
                value={formData.bank_id}
                onChange={handleChange}
                style={inputStyle}
                required
              >
                <option value="">Banka Seçiniz</option>
                {banks.map(bank => (
                  <option key={bank.id} value={bank.id}>
                    {bank.bank_name} - {bank.account_holder}
                  </option>
                ))}
              </select>
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="currency" style={labelStyle}>
                Para Birimi *
              </label>
              <input
                type="text"
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Örn: TRY, USD, EUR"
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="iban" style={labelStyle}>
                IBAN *
              </label>
              <input
                type="text"
                id="iban"
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                style={ibanInputStyle}
                placeholder="TR12 3456 7890 1234 5678 9012 34"
                required
              />
            </div>

            <div style={checkboxContainerStyle}>
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                style={checkboxStyle}
              />
              <label htmlFor="is_active" style={checkboxLabelStyle}>
                Aktif
              </label>
            </div>

            <div style={buttonContainerStyle}>
              <button
                type="button"
                onClick={() => navigate('/admin/bank-accounts')}
                style={cancelButtonStyle}
                disabled={loading}
              >
                İptal
              </button>
              <button
                type="submit"
                style={submitButtonStyle}
                disabled={loading}
              >
                {loading ? 'İşleniyor...' : (isEdit ? 'Güncelle' : 'Ekle')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default BankAccountForm;